import React, { useMemo } from 'react';
import { Activity, Users, AlertTriangle, CheckCircle, X } from 'lucide-react';

interface ProcessMapStatisticsProps {
  processes: any[];
  elements: any;
  currentMap: any;
  selectedLevel?: number;
  finalProcessSteps?: any[]; // Real-time data from SwimlanesVisualization
  onClose?: () => void;
}

interface GapInfo {
  stepName: string;
  stepNumber: number;
  missingDescription: boolean;
  missingStakeholder: boolean;
}

const ProcessMapStatistics: React.FC<ProcessMapStatisticsProps> = ({
  processes,
  elements,
  currentMap,
  selectedLevel = 3,
  finalProcessSteps = [],
  onClose
}) => {


  const totalSteps: number = currentMap?.generation_metadata?.process_count ?? 0;

  const hasRealData = totalSteps > 0;

  if (!hasRealData) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-gray-400" />
          Map Statistics
        </h3>
        <div className="text-center py-8">
          <div className="text-gray-400 text-sm">
            No process map data available
          </div>
          <div className="text-gray-500 text-xs mt-2">
            Generate a process map to see statistics
          </div>
        </div>
      </div>
    );
  }

  // Calculate stakeholders using same logic as SwimlanesVisualization to match visible count
  const calculateStakeholders = () => {
    // For Level 3, use a more conservative count that matches the visible swimlanes
    if (selectedLevel === 3) {
      // Count from swimlanes data (preferred for Level 3)
      if (currentMap?.map_data?.swimlanes && currentMap.map_data.swimlanes.length > 0) {
        return currentMap.map_data.swimlanes.length;
      }

      // Build roles exactly like SwimlanesVisualization does
      const exactStakeholders = new Set<string>();

      if (processes && processes.length > 0) {
        processes.forEach(process => {
          if (process.actor && process.actor !== 'Unassigned' && process.actor !== 'Process Owner') {
            if (process.actor.length > 2 && !process.actor.includes('Generated')) {
              exactStakeholders.add(process.actor);
            }
          }
          if (process.assignee && process.assignee !== 'Unassigned' && process.assignee !== 'Process Owner' && !process.actor) {
            if (process.assignee.length > 2 && !process.assignee.includes('Generated')) {
              exactStakeholders.add(process.assignee);
            }
          }
        });
      }

      // Limit to 8 like SwimlanesVisualization does
      const roles = Array.from(exactStakeholders).slice(0, 8);

      if (roles.length === 0) {
        // Default roles for Level 3
        return 3; // Process Owner + Stakeholder + System Layer
      }

      return roles.length;
    }

    // For other levels, use the original logic
    const metadataCount = currentMap?.generation_metadata?.stakeholder_count;
    if (metadataCount && metadataCount > 0) return metadataCount;

    // Count from processes data
    if (processes && processes.length > 0) {
      const uniqueFromProcesses = new Set();
      processes.forEach(process => {
        if (process.actor && process.actor !== 'Process Owner' && process.actor !== 'Unassigned') {
          uniqueFromProcesses.add(process.actor);
        }
        if (process.assignee && process.assignee !== 'Process Owner' && process.assignee !== 'Unassigned') {
          uniqueFromProcesses.add(process.assignee);
        }
      });
      if (uniqueFromProcesses.size > 0) return uniqueFromProcesses.size;
    }

    // Count from finalProcessSteps if available
    if (finalProcessSteps && finalProcessSteps.length > 0) {
      const uniqueFromSteps = new Set();
      finalProcessSteps.forEach(step => {
        if (step.assignee && step.assignee !== 'Process Owner' && step.assignee !== 'Unassigned') {
          uniqueFromSteps.add(step.assignee);
        }
      });
      if (uniqueFromSteps.size > 0) return uniqueFromSteps.size;
    }

    // Default based on level if no data found
    return selectedLevel === 1 ? 2 : selectedLevel === 2 ? 3 : selectedLevel === 3 ? 4 : 2;
  };

  const uniqueAssignees = calculateStakeholders();

  // Calculate gaps in process steps (same logic as backend gapSyncService)
  const gapAnalysis = useMemo(() => {
    const gaps: GapInfo[] = [];
    let stepsWithGaps = 0;
    let missingDescriptionCount = 0;
    let missingStakeholderCount = 0;

    const mapProcesses = currentMap?.map_data?.elements?.processes || processes || [];

    mapProcesses.forEach((step: any, index: number) => {
      const stepName = step.name || step.label || `Step ${index + 1}`;
      const stepDesc = step.description || step.full_text || step.detailed_description || '';
      const stakeholder = step.stakeholder || step.assignee || step.actor || '';
      const stepNumber = index + 1;

      const hasMissingDesc = !stepDesc || stepDesc.length < 20;
      const hasMissingStakeholder = !stakeholder || stakeholder === 'TBD' || stakeholder === 'Unknown' || stakeholder === 'Unassigned';

      if (hasMissingDesc) missingDescriptionCount++;
      if (hasMissingStakeholder) missingStakeholderCount++;

      if (hasMissingDesc || hasMissingStakeholder) {
        stepsWithGaps++;
        gaps.push({
          stepName,
          stepNumber,
          missingDescription: hasMissingDesc,
          missingStakeholder: hasMissingStakeholder
        });
      }
    });

    const totalStepsCount = mapProcesses.length;
    const stepsWithoutGaps = totalStepsCount - stepsWithGaps;
    const gapPercentage = totalStepsCount > 0 ? Math.round((stepsWithGaps / totalStepsCount) * 100) : 0;

    return {
      gaps,
      stepsWithGaps,
      stepsWithoutGaps,
      totalSteps: totalStepsCount,
      missingDescriptionCount,
      missingStakeholderCount,
      gapPercentage,
      hasNoGaps: stepsWithGaps === 0
    };
  }, [currentMap?.map_data?.elements?.processes, processes]);

  const createdDate = currentMap?.created_at
    ? new Date(currentMap.created_at).toLocaleDateString()
    : new Date().toLocaleDateString();

  const stats = [
    {
      label: 'Stakeholders',
      value: uniqueAssignees,
      icon: Users,
      color: 'text-purple-600'
    }
  ];

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-blue-600" />
          Map Statistics
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
          </button>
        )}
      </div>
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 gap-3 text-sm">
          <div className="flex justify-between items-start">
            <span className="text-gray-600">Process ID:</span>
            <span className="font-mono text-blue-600 text-xs bg-blue-50 px-2 py-1 rounded">
              {currentMap?.process_id || 'Not Available'}
            </span>
          </div>
          <div className="flex justify-between items-start">
            <span className="text-gray-600">Step ID Format:</span>
            <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {currentMap?.process_id ? `${currentMap.process_id}_STEP_XXX` : 'ID_STEP_XXX'}
            </span>
          </div>
        </div>
      </div>


      <div className="space-y-3">
        {stats.map((stat, index) => (
          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            <div className="flex items-center">
              <stat.icon className={`w-4 h-4 mr-2 ${stat.color}`} />
              <span className="text-sm font-medium text-gray-700">{stat.label}</span>
            </div>
            <span className="text-sm font-bold text-gray-900">{stat.value}</span>
          </div>
        ))}
      </div>

      {/* Gap Status Section */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          {gapAnalysis.hasNoGaps ? (
            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
          ) : (
            <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" />
          )}
          Gap Status
        </h4>

        {gapAnalysis.hasNoGaps ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-800">All steps complete!</span>
            </div>
            <p className="text-xs text-green-600 mt-1">
              All {gapAnalysis.totalSteps} steps have descriptions and stakeholders assigned.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Summary */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-amber-800">
                  {gapAnalysis.stepsWithGaps} of {gapAnalysis.totalSteps} steps have gaps
                </span>
                <span className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-1 rounded">
                  {gapAnalysis.gapPercentage}%
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-amber-200 rounded-full h-2 mb-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${100 - gapAnalysis.gapPercentage}%` }}
                />
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center text-amber-700">
                  <span className="w-2 h-2 bg-red-400 rounded-full mr-1.5"></span>
                  Missing Description: {gapAnalysis.missingDescriptionCount}
                </div>
                <div className="flex items-center text-amber-700">
                  <span className="w-2 h-2 bg-orange-400 rounded-full mr-1.5"></span>
                  Missing Stakeholder: {gapAnalysis.missingStakeholderCount}
                </div>
              </div>
            </div>

            {/* Gap Details - Always show in compact format */}
            {gapAnalysis.gaps.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-2 max-h-60 overflow-y-auto">
                <h5 className="text-xs font-semibold text-gray-700 mb-2 px-1 sticky top-0 bg-gray-50">
                  Steps with Gaps ({gapAnalysis.gaps.length}):
                </h5>
                <div className="space-y-1.5">
                  {gapAnalysis.gaps.map((gap, idx) => (
                    <div key={idx} className="text-xs bg-white p-2 rounded border border-gray-200 hover:border-amber-300 transition-colors">
                      <div className="flex items-start justify-between mb-1">
                        <span className="font-semibold text-gray-800">Step {gap.stepNumber}</span>
                        <div className="flex gap-1">
                          {gap.missingDescription && (
                            <span className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded text-[9px] font-medium">
                              DESC
                            </span>
                          )}
                          {gap.missingStakeholder && (
                            <span className="text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded text-[9px] font-medium">
                              STAKE
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-gray-600 text-[11px] leading-tight truncate">
                        {gap.stepName}
                      </p>
                      <div className="mt-1 text-[10px] text-gray-500">
                        {gap.missingDescription && gap.missingStakeholder ? (
                          <span>⚠️ Missing both description & stakeholder</span>
                        ) : gap.missingDescription ? (
                          <span>📝 Missing detailed description</span>
                        ) : (
                          <span>👤 Missing stakeholder assignment</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcessMapStatistics;